import type { PainPoint, Cut, BodyShape, HeightBand } from '@/types/quiz'

/**
 * Short phrase for each pain point, used to build the "we heard you" validation
 * line on the result screen. Reads naturally inside a comma-joined list.
 */
const PAIN_PHRASE: Record<PainPoint, string> = {
  waist_gap:     'gapping waistbands',
  tight_thighs:  'tight thighs',
  sized_up_hips: 'sizing up to fit your hips',
  crotch_rips:   'ripped crotch',
  waist_digs:    'waistbands digging in',
  buttons_pop:   'popping buttons',
  baggy_legs:    'baggy legs',
  length_wrong:  'lengths that never work',
  wears_mens:    'defaulting to men\'s pants',
  none:          '',
}

/** Lead-in line per recommended cut. Brand voice: direct, empathetic, warm. */
const CUT_LEAD_IN: Record<Cut, string> = {
  zali:     'Our Zali style was made for exactly this.',
  mila:     'Our Mila style was built around this shape.',
  beatrice: 'Our Beatrice style was designed with this in mind.',
}

// (Beatrice + tall now uses the standard Beatrice lead-in. The trade-off
// explanation lives on the product card itself as a footnote underneath the
// Learn-more button - that puts it where the action is, not buried at the
// top of the screen.)

/**
 * Build the validation line shown at the top of the result screen.
 *
 * The line mirrors the customer's selected pain points back to them and then
 * hands off to the recommended cut. If the user selected only universal /
 * "none" signals, we skip the echo and open on the cut's lead-in directly.
 *
 * Examples:
 *   painPoints: ['waist_gap', 'tight_thighs'], cut: 'zali'
 *   -> "You told us about gapping waistbands and tight thighs. Our Zali pant
 *       was made for exactly this."
 *
 *   painPoints: ['none'], cut: 'mila'
 *   -> "Our Mila pant was built around this shape."
 */
export function buildValidationLine(
  painPoints: readonly PainPoint[],
  cut: Cut,
  _bodyShape: BodyShape,
  _heightBand: HeightBand,
): string {
  const leadIn = CUT_LEAD_IN[cut]

  const phrases = painPoints
    .map((p) => PAIN_PHRASE[p])
    .filter((s) => s.length > 0)

  if (phrases.length === 0) {
    return leadIn
  }

  const joined = joinWithAnd(phrases.slice(0, 3))
  return `You told us about ${joined}. ${leadIn}`
}

function joinWithAnd(items: readonly string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}
